/** 表示用フォーマッタ群 */
import type { ArrivalQuality } from '@mbus/shared';

/** "何分後" の人間向け表現 */
export function minutesLabel(min: number): string {
  if (min <= 0) return 'まもなく';
  if (min < 60) return `${min}分`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${h}時間` : `${h}時間${m}分`;
}

/** ISO8601 → "HH:MM" (JST 表示。サーバは JST の ISO を返す) */
export function hhmm(iso: string | null): string {
  if (!iso) return '--:--';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '--:--';
  return d.toLocaleTimeString('ja-JP', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Tokyo',
  });
}

/** 遅延の表現。+3分 / 定刻 / -1分 */
export function delayLabel(delaySeconds: number | null): string | null {
  if (delaySeconds === null) return null;
  const min = Math.round(delaySeconds / 60);
  if (min === 0) return '定刻';
  return min > 0 ? `+${min}分遅れ` : `${-min}分早発`;
}

/** データ品質の説明ラベル */
export function qualityLabel(quality: ArrivalQuality): string {
  switch (quality) {
    case 'realtime':
      return 'リアルタイム';
    case 'predicted':
      return '予測';
    case 'scheduled':
      return '時刻表';
    default:
      return '不明';
  }
}

/** 混雑度 (GTFS-RT OccupancyStatus) → 日本語 */
export function occupancyLabel(status: string | null | undefined): string | null {
  if (!status) return null;
  const map: Record<string, string> = {
    EMPTY: '空いています',
    MANY_SEATS_AVAILABLE: '座席に余裕あり',
    FEW_SEATS_AVAILABLE: '座席わずか',
    STANDING_ROOM_ONLY: '立ち席あり',
    CRUSHED_STANDING_ROOM_ONLY: '混雑',
    FULL: '満員',
    NOT_ACCEPTING_PASSENGERS: '乗車できません',
  };
  return map[status] ?? null;
}

/** 徒歩分 → "徒歩3分" */
export function walkLabel(min: number | null | undefined): string | null {
  if (min === null || min === undefined) return null;
  return `徒歩${Math.max(1, Math.round(min))}分`;
}

/** 今日の運行日 (YYYY-MM-DD, JST)。深夜帯 (〜3時) は前日扱いにしない — サーバ側が便ごとに正しい service date を返すため、既定値としてのみ使う */
export function todayServiceDate(): string {
  const now = new Date();
  const jst = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Tokyo' }));
  const y = jst.getFullYear();
  const m = String(jst.getMonth() + 1).padStart(2, '0');
  const d = String(jst.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
